import { DEFAULT_PAGE_SIZE } from "@/core/constants/pagination";
import type { IHttpClient } from "@/core/interfaces";
import {
  extractDetail,
  extractItems,
  extractTotal,
  isRecord,
  readFirstText,
  toPaginatedQuery,
} from "@/core/utils";
import { parseContentDispositionFileName } from "@/modules/export";
import type { ExportedFile, ExportFormat, XlsxLayout } from "@/modules/export";
import { Registry } from "@/shared/infra/dependency-injection/registry";
import type {
  CriarFichaFinanceiraPayload,
  FichaAnexo,
  FichaAnexoArquivo,
  IncluirFichaAnexoPayload,
  FichaFinanceiraCriada,
  FichaFinanceiraDetalhe,
  FichaPrevia,
  FichaFinanceiraItem,
  FichaCustosFinanceiro,
  FichaCustosTipoObra,
  FichaInformacoesEmbarcacao,
  FichaObraResumo,
  FichasFinanceirasPage,
  ListFichasFinanceirasParams,
} from "./financeiro-fichas.types";
import {
  DEFAULT_FICHAS_FINANCEIRAS_SORT,
  MAX_FICHAS_FINANCEIRAS_PAGE_SIZE,
} from "./financeiro-fichas.types";

/**
 * Contrato consumido do hub-bff: consulta (HU 2679, `hub.financeiro:view-fmm`) e geração
 * (HU 2684, `hub.financeiro:add-ficha`: prévia e criação) e anexos (HU 2688: listar e baixar com
 * `hub.financeiro:view-fmm`; incluir e remover com `hub.financeiro:add-ficha`), além da exportação (HU 2689,
 * `hub.financeiro:edit-fmm`).
 */
export const FINANCEIRO_FICHAS_PATHS = {
  listar: (obraId: string) =>
    `/api/financeiro/obras/${encodeURIComponent(obraId)}/fichas-financeiras`,
  criar: (obraId: string) =>
    `/api/financeiro/obras/${encodeURIComponent(obraId)}/fichas-financeiras`,
  previa: (obraId: string) =>
    `/api/financeiro/obras/${encodeURIComponent(obraId)}/fichas-financeiras/previa`,
  detalhar: (obraId: string, fichaId: string) =>
    `/api/financeiro/obras/${encodeURIComponent(obraId)}/fichas-financeiras/${encodeURIComponent(fichaId)}`,
  exportar: (obraId: string, fichaId: string) =>
    `/api/financeiro/obras/${encodeURIComponent(obraId)}/fichas-financeiras/${encodeURIComponent(fichaId)}/exportacao`,
  anexos: (obraId: string, fichaId: string) =>
    `/api/financeiro/obras/${encodeURIComponent(obraId)}/fichas-financeiras/${encodeURIComponent(fichaId)}/anexos`,
  anexo: (obraId: string, fichaId: string, anexoId: string) =>
    `/api/financeiro/obras/${encodeURIComponent(obraId)}/fichas-financeiras/${encodeURIComponent(fichaId)}/anexos/${encodeURIComponent(anexoId)}`,
} as const;

function getHttpClient(): IHttpClient {
  return Registry.getInstance().inject<IHttpClient>("HttpClient");
}

/** Texto do campo; aceita também um objeto com `nome` (pessoa/cliente); vazio vira `null`. */
function readLabel(raw: Record<string, unknown>, key: string): string | null {
  const value = raw[key];
  if (typeof value === "string") return value.trim() || null;
  if (typeof value === "number") return String(value);
  if (isRecord(value)) return readFirstText(value, "nome", "name", "descricao").trim() || null;
  return null;
}

function readNumber(raw: Record<string, unknown>, key: string): number | null {
  const value = raw[key];
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

/** Bloco `obra` da ficha; ausente na resposta vira campos vazios (a tela mostra "—"). */
function toObra(raw: unknown): FichaObraResumo {
  const obra = isRecord(raw) ? raw : {};
  const etapas = isRecord(obra.etapas) ? obra.etapas : {};

  return {
    codigo: readLabel(obra, "codigo") ?? "",
    embarcacao: readLabel(obra, "embarcacao") ?? "",
    condicao: readLabel(obra, "condicao"),
    localEstimado: readLabel(obra, "localEstimado"),
    empresaGrupo: readLabel(obra, "empresaGrupo"),
    inicio: readFirstText(obra, "inicio").trim(),
    fim: readFirstText(obra, "fim").trim(),
    duracaoDias: readNumber(obra, "duracaoDias") ?? 0,
    etapas: {
      obraDias: readNumber(etapas, "obraDias"),
      testesDias: readNumber(etapas, "testesDias"),
      aceitacaoDias: readNumber(etapas, "aceitacaoDias"),
    },
  };
}

/** Bloco `informacoesEmbarcacao`; ausente vira tudo vazio (a tela mostra "—"). */
function toInformacoesEmbarcacao(raw: unknown): FichaInformacoesEmbarcacao {
  const info = isRecord(raw) ? raw : {};

  return {
    bandeira: readLabel(info, "bandeira"),
    imo: readLabel(info, "imo"),
    deadweight: readNumber(info, "deadweight"),
    arqueacaoBruta: readNumber(info, "arqueacaoBruta"),
    arqueacaoLiquida: readNumber(info, "arqueacaoLiquida"),
    loa: readNumber(info, "loa"),
    bocaMoldada: readNumber(info, "bocaMoldada"),
    caladoMaximo: readNumber(info, "caladoMaximo"),
    caladoPrevistoEntrada: readNumber(info, "caladoPrevistoEntrada"),
    deslocamentoPrevistoEntrada: readNumber(info, "deslocamentoPrevistoEntrada"),
    convesComprimento: readNumber(info, "convesComprimento"),
    convesLargura: readNumber(info, "convesLargura"),
    convesAreaTotal: readNumber(info, "convesAreaTotal"),
    convesResistencia: readNumber(info, "convesResistencia"),
    convesCapacidade: readNumber(info, "convesCapacidade"),
  };
}

function toCustosTipoObra(raw: unknown): FichaCustosTipoObra | null {
  if (!isRecord(raw)) return null;

  const id = readFirstText(raw, "id").trim();
  if (!id) return null;

  const escopos = Array.isArray(raw.escoposMacro) ? raw.escoposMacro : [];

  return {
    id,
    codigo: readLabel(raw, "codigo") ?? "",
    nome: readLabel(raw, "nome") ?? "",
    dominante: raw.dominante === true,
    escoposMacro: escopos.flatMap((escopo) => {
      if (!isRecord(escopo)) return [];
      const escopoId = readFirstText(escopo, "id").trim();
      return escopoId ? [{ id: escopoId, nome: readLabel(escopo, "nome") ?? "" }] : [];
    }),
  };
}

/** Bloco `custosFinanceiro`; ausente vira vazio (a tela mostra "—" e a tabela só com totais). */
function toCustosFinanceiro(raw: unknown): FichaCustosFinanceiro {
  const custos = isRecord(raw) ? raw : {};
  const tipos = Array.isArray(custos.tiposObra) ? custos.tiposObra : [];

  return {
    descricaoObra: readLabel(custos, "descricaoObra"),
    centroCusto: readLabel(custos, "centroCusto"),
    tiposObra: tipos
      .map(toCustosTipoObra)
      .filter((tipo): tipo is FichaCustosTipoObra => tipo !== null),
  };
}

function toItem(raw: unknown): FichaFinanceiraItem | null {
  if (!isRecord(raw)) return null;

  const id = readFirstText(raw, "id").trim();
  if (!id) return null;

  return {
    id,
    obraId: readFirstText(raw, "obraId").trim(),
    criadoEm: readFirstText(raw, "criadoEm").trim(),
    statusFel: readLabel(raw, "statusFel"),
    solicitante: readLabel(raw, "solicitante") ?? "",
    solicitanteEmail: readLabel(raw, "solicitanteEmail"),
  };
}

/**
 * Página de fichas da obra: o servidor pagina e ordena com o mesmo contrato da listagem de obras
 * do Financeiro (`page`, `pageSize`, `sort_by`, `sort_direction`); sem ordem informada, `criadoEm` desc.
 */
export async function listFichasFinanceiras(
  obraId: string,
  {
    page = 1,
    pageSize = DEFAULT_PAGE_SIZE,
    sort = DEFAULT_FICHAS_FINANCEIRAS_SORT,
    signal,
  }: ListFichasFinanceirasParams = {},
): Promise<FichasFinanceirasPage> {
  const response = await getHttpClient().get<unknown>(FINANCEIRO_FICHAS_PATHS.listar(obraId), {
    signal,
    params: toPaginatedQuery({
      page: Math.max(1, Math.trunc(page)),
      pageSize: Math.min(MAX_FICHAS_FINANCEIRAS_PAGE_SIZE, Math.max(1, Math.trunc(pageSize))),
      sort,
    }),
  });

  const items = extractItems(response.data)
    .map(toItem)
    .filter((item): item is FichaFinanceiraItem => item !== null);

  return { items, total: extractTotal(extractDetail(response.data)) ?? items.length };
}

export async function getFichaFinanceira(
  obraId: string,
  fichaId: string,
  signal?: AbortSignal,
): Promise<FichaFinanceiraDetalhe> {
  const response = await getHttpClient().get<unknown>(
    FINANCEIRO_FICHAS_PATHS.detalhar(obraId, fichaId),
    { signal },
  );

  const raw = extractDetail(response.data);
  const record = isRecord(raw) ? raw : {};
  const dados = record.dadosFicha;

  return {
    id: readFirstText(record, "id").trim() || fichaId,
    obraId: readFirstText(record, "obraId").trim() || obraId,
    criadoEm: readFirstText(record, "criadoEm").trim(),
    solicitante: readLabel(record, "solicitante") ?? "",
    statusFel: readLabel(record, "statusFel"),
    obra: toObra(record.obra),
    informacoesEmbarcacao: toInformacoesEmbarcacao(record.informacoesEmbarcacao),
    custosFinanceiro: toCustosFinanceiro(record.custosFinanceiro),
    clienteFinal: readLabel(record, "clienteFinal"),
    contrato: readLabel(record, "contrato"),
    diretorProjeto: readLabel(record, "diretorProjeto"),
    responsavelPreparacao: readLabel(record, "responsavelPreparacao"),
    responsavelExecucao: readLabel(record, "responsavelExecucao"),
    coordenador: readLabel(record, "coordenador"),
    dadosFicha: isRecord(dados) && !Array.isArray(dados) ? dados : null,
  };
}

/**
 * Arquivo da ficha gerado no servidor (PDF por padrão, XLSX ou CSV); o nome vem do
 * `Content-Disposition` (`null` se ausente). `xlsxLayout` só vale para o XLSX.
 */
export async function exportFichaFinanceira(
  obraId: string,
  fichaId: string,
  format: ExportFormat = "pdf",
  options?: { signal?: AbortSignal; xlsxLayout?: XlsxLayout },
): Promise<ExportedFile> {
  const response = await getHttpClient().get<ArrayBuffer>(
    FINANCEIRO_FICHAS_PATHS.exportar(obraId, fichaId),
    {
      signal: options?.signal,
      params: {
        formato: format,
        ...(format === "xlsx" && options?.xlsxLayout ? { layout: options.xlsxLayout } : {}),
      },
      responseType: "arraybuffer",
    },
  );

  return {
    content: new Uint8Array(response.data),
    fileName: parseContentDispositionFileName(response.headers?.["content-disposition"]),
    mimeType: response.headers?.["content-type"] ?? null,
  };
}

/** Prévia da ficha (HU 2684): dados lidos do banco, sem gravar nada. */
export async function getPreviaFichaFinanceira(
  obraId: string,
  signal?: AbortSignal,
): Promise<FichaPrevia> {
  const response = await getHttpClient().get<unknown>(FINANCEIRO_FICHAS_PATHS.previa(obraId), {
    signal,
  });

  const raw = extractDetail(response.data);
  const record = isRecord(raw) ? raw : {};

  return {
    elaboradoEm: readFirstText(record, "elaboradoEm").trim(),
    obra: toObra(record.obra),
    informacoesEmbarcacao: toInformacoesEmbarcacao(record.informacoesEmbarcacao),
    custosFinanceiro: toCustosFinanceiro(record.custosFinanceiro),
  };
}

/**
 * Grava a ficha (HU 2684): `201` com a identificação da ficha criada. `idempotencyKey` identifica a tentativa de salvar: o bff devolve a resposta guardada quando a
 * mesma chave chega de novo, então uma retentativa depois de falha de rede não duplica a ficha.
 */
export async function criarFichaFinanceira(
  obraId: string,
  payload: CriarFichaFinanceiraPayload,
  { signal, idempotencyKey }: { signal?: AbortSignal; idempotencyKey?: string } = {},
): Promise<FichaFinanceiraCriada> {
  const response = await getHttpClient().post<unknown>(
    FINANCEIRO_FICHAS_PATHS.criar(obraId),
    payload,
    { signal, headers: idempotencyKey ? { "Idempotency-Key": idempotencyKey } : undefined },
  );

  const raw = extractDetail(response.data);
  const record = isRecord(raw) ? raw : {};

  return {
    id: readFirstText(record, "id").trim(),
    elaboradoEm: readFirstText(record, "elaboradoEm").trim(),
    geradoPor: readLabel(record, "geradoPor") ?? "",
  };
}

function toAnexo(raw: unknown): FichaAnexo | null {
  if (!isRecord(raw)) return null;

  const id = readFirstText(raw, "id").trim();
  if (!id) return null;

  return {
    id,
    identificacao: readLabel(raw, "identificacao") ?? "",
    nomeArquivo: readLabel(raw, "nomeArquivo") ?? "",
    formato: readLabel(raw, "formato") ?? "",
    tamanhoBytes: readNumber(raw, "tamanhoBytes") ?? 0,
  };
}

/** Anexos da ficha (HU 2688), na ordem do servidor. */
export async function listFichaAnexos(
  obraId: string,
  fichaId: string,
  signal?: AbortSignal,
): Promise<FichaAnexo[]> {
  const response = await getHttpClient().get<unknown>(
    FINANCEIRO_FICHAS_PATHS.anexos(obraId, fichaId),
    { signal },
  );

  return extractItems(response.data)
    .map(toAnexo)
    .filter((anexo): anexo is FichaAnexo => anexo !== null);
}

/**
 * Inclui os anexos do lote numa única requisição (`multipart/form-data`: `arquivos` e `identificacoes`,
 * um par por arquivo e na mesma ordem); `201` com `{ items, total }`. O servidor grava tudo ou nada.
 * O `Content-Type` explícito evita que o cliente HTTP (JSON por padrão) serialize o `FormData`
 * como JSON; o navegador acrescenta o `boundary`.
 */
export async function incluirFichaAnexos(
  obraId: string,
  fichaId: string,
  payloads: IncluirFichaAnexoPayload[],
  signal?: AbortSignal,
): Promise<FichaAnexo[]> {
  const body = new FormData();
  for (const payload of payloads) {
    body.append("arquivos", payload.arquivo, payload.arquivo.name);
    body.append("identificacoes", payload.identificacao);
  }

  const response = await getHttpClient().post<unknown>(
    FINANCEIRO_FICHAS_PATHS.anexos(obraId, fichaId),
    body,
    { signal, headers: { "Content-Type": "multipart/form-data" } },
  );

  return extractItems(response.data)
    .map(toAnexo)
    .filter((anexo): anexo is FichaAnexo => anexo !== null);
}

/** Remove o anexo da ficha; `204`. */
export async function removerFichaAnexo(
  obraId: string,
  fichaId: string,
  anexoId: string,
  signal?: AbortSignal,
): Promise<void> {
  await getHttpClient().delete<unknown>(FINANCEIRO_FICHAS_PATHS.anexo(obraId, fichaId, anexoId), {
    signal,
  });
}

/** Binário do anexo (`attachment`), para baixar ou abrir. */
export async function baixarFichaAnexo(
  obraId: string,
  fichaId: string,
  anexoId: string,
  signal?: AbortSignal,
): Promise<FichaAnexoArquivo> {
  const response = await getHttpClient().get<ArrayBuffer>(
    FINANCEIRO_FICHAS_PATHS.anexo(obraId, fichaId, anexoId),
    { signal, responseType: "arraybuffer" },
  );

  return {
    content: new Uint8Array(response.data),
    fileName: parseContentDispositionFileName(response.headers?.["content-disposition"]),
    mimeType: response.headers?.["content-type"] ?? null,
  };
}
